import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { ListBulletsIcon, ListNumbersIcon, TextBIcon, TextItalicIcon } from "@phosphor-icons/react";

type TSlideOutlineEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isReadOnly?: boolean;
  isSelected?: boolean;
};

export function SlideOutlineEditor(props: TSlideOutlineEditorProps) {
  const { t } = useTranslation("projectHome");
  const {
    value,
    onChange,
    placeholder = t("presentation.keyPointsPlaceholder"),
    isReadOnly = false,
    isSelected = true
  } = props;

  const effectiveReadOnly = isReadOnly || !isSelected;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        hardBreak: false
      }),
      Placeholder.configure({ placeholder })
    ],
    content: value,
    editable: !effectiveReadOnly,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "typo-body-md w-full max-w-none bg-transparent text-foreground outline-hidden"
      }
    }
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value);
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!effectiveReadOnly, false);
  }, [editor, effectiveReadOnly]);

  if (!editor) return null;

  const tools = [
    {
      id: "bold",
      label: t("presentationBuilder.bold"),
      icon: TextBIcon,
      run: () => editor.chain().focus().toggleBold().run()
    },
    {
      id: "italic",
      label: t("presentationBuilder.italic"),
      icon: TextItalicIcon,
      run: () => editor.chain().focus().toggleItalic().run()
    },
    {
      id: "bulletList",
      testId: "bullet-list",
      label: t("presentationBuilder.bulletList"),
      icon: ListBulletsIcon,
      run: () => editor.chain().focus().toggleBulletList().run()
    },
    {
      id: "orderedList",
      testId: "numbered-list",
      label: t("presentationBuilder.numberedList"),
      icon: ListNumbersIcon,
      run: () => editor.chain().focus().toggleOrderedList().run()
    }
  ];
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {!isReadOnly && isSelected && (
        <div
          role="toolbar"
          aria-label={t("presentationBuilder.formatting")}
          className="flex w-fit items-center gap-0.5 rounded-lg bg-muted/60 p-0.5"
        >
          {tools.map((tool) => (
            <Button
              key={tool.id}
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={tool.label}
              title={tool.label}
              aria-pressed={editor.isActive(tool.id)}
              data-testid={`slide-outline-editor-${tool.testId ?? tool.id}-button`}
              onClick={tool.run}
              className="text-muted-foreground aria-pressed:bg-background aria-pressed:text-foreground aria-pressed:shadow-xs"
            >
              <tool.icon />
            </Button>
          ))}
        </div>
      )}
      <EditorContent
        editor={editor}
        className={cn(
          "-mx-2 min-h-0 flex-1 overflow-auto rounded-lg px-2 py-1 text-foreground transition-colors",
          isSelected && !isReadOnly && "focus-within:bg-muted/40",
          "[&_.ProseMirror]:min-h-24 [&_.ProseMirror]:p-0 focus:[&_.ProseMirror]:outline-hidden [&_.ProseMirror_p]:my-1 [&_.ProseMirror_ul]:my-1 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:ps-5 [&_.ProseMirror_ol]:my-1 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:ps-5 [&_.ProseMirror_li]:my-0.5",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]"
        )}
      />
    </div>
  );
}
